import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useTx } from '../../hooks/useTx';
import { useToast } from '../../context/ToastContext';
import { usd, toUSDT, numUSDT, fmtDate } from '../../utils/format';
import { getLastRoundTime } from '../../utils/logs';

// One self-contained card per pool: eligible · accumulated · per-user approx ·
// round · last distributed date + distribute input + reset button.
export default function PoolCard({
  poolType, // 1=silver 2=gold 3=diamond 4=special
  name,
  mark,
  cls,
  accumulated, // BigNumber
  eligible, // number
  distributeMethod,
  resetMethod,
  onDone,
  reloadKey,
}) {
  const { evittaRead } = useContracts();
  const { run, pending } = useTx();
  const toast = useToast();
  const [amount, setAmount] = useState('');

  const [round, setRound] = useState(null);
  const [latestPerUser, setLatestPerUser] = useState(null);
  const [lastDate, setLastDate] = useState(undefined); // undefined=loading, 0=never, n=ts

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // round + latest per-user from view fn; last date from event log
  useEffect(() => {
    if (!evittaRead) return undefined;
    let cancelled = false;
    (async () => {
      const info = await evittaRead.getPoolDistributionInfo(poolType).catch(() => null);
      let cur = 0;
      if (!cancelled && mounted.current && info) {
        cur = Number(info.currentRound);
        setRound(cur);
        setLatestPerUser(info.latestAmountPerUser);
      }

      // No rounds yet -> genuinely "never", skip the log scan.
      if (cur <= 0) {
        if (!cancelled && mounted.current) setLastDate(0);
        return;
      }

      // Last distributed date via PoolIncomeRoundCreated event (logs-capable RPC).
      const ts = await getLastRoundTime(poolType);
      if (!cancelled && mounted.current) setLastDate(ts);
    })();
    return () => { cancelled = true; };
  }, [evittaRead, poolType, reloadKey]);

  // accumulated is a wei BigNumber. Divide by eligible IN wei, then format once.
  const accBN = ethers.BigNumber.isBigNumber(accumulated)
    ? accumulated
    : ethers.BigNumber.from(0);
  const perUserWei = eligible > 0 ? accBN.div(eligible) : ethers.constants.Zero;
  const perUserApprox = numUSDT(perUserWei); // plain number, used for the input placeholder

  const distribute = () => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      toast.error('Enter a valid per-user amount.');
      return;
    }
    run({
      method: distributeMethod,
      args: [toUSDT(amount)],
      successMsg: `${name} round distributed.`,
      onDone: () => {
        setAmount('');
        if (onDone) onDone();
      },
    });
  };

  const reset = () =>
    run({ method: resetMethod, args: [], successMsg: `${name} accumulation reset.`, onDone });

  return (
    <div className={`card pool ${cls}`} style={{ marginBottom: 12 }}>
      {/* Header */}
      <div className="card-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="ring">{mark}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{name}</div>
            <span className="pill" style={{ marginTop: 4 }}>{eligible} eligible</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="stat-label">Accumulated</div>
          <div className="stat-value gold" style={{ fontSize: 18 }}>{usd(accumulated)}</div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid-3" style={{ marginTop: 14 }}>
        <Mini label="Per user ≈" value={usd(perUserWei)} />
        <Mini label="Round" value={round == null ? '—' : `#${round}`} />
        <Mini label="Last per user" value={latestPerUser != null ? usd(latestPerUser) : '—'} />
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 8, textAlign: 'center' }}>
        Last distributed:{' '}
        {lastDate === undefined
          ? 'loading…'
          : lastDate > 0
          ? fmtDate(lastDate)
          : round > 0
          ? '—'
          : 'never'}
      </div>

      {/* Distribute */}
      <div className="field">
        <label>Amount per user (USDT)</label>
        <input
          inputMode="decimal"
          placeholder={perUserApprox > 0 ? `≈ ${perUserApprox}` : '0.0'}
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
        />
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button className="btn" style={{ flex: 1 }} onClick={distribute} disabled={pending}>
          {pending ? <span className="spin-inline" /> : null}
          {pending ? 'Processing…' : 'Distribute'}
        </button>
        <button
          className="btn ghost"
          style={{ flex: '0 0 auto', width: 'auto', color: 'var(--err)', borderColor: 'rgba(255,90,95,0.4)' }}
          onClick={reset}
          disabled={pending}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="dim" style={{ fontSize: 10.5 }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 13, marginTop: 2 }}>{value}</div>
    </div>
  );
}
